/**
 * Who is signed in, for the whole app (SB-23; ADR-0004, ADR-0005).
 *
 * At startup the app asks `/api/auth/me`. While it says `auth_enabled: false` nothing changes:
 * no sign-in page, no user menu, and every page renders as before. With it on, a person who is
 * not signed in goes to the sign-in page. A token that expired while the browser was closed is
 * refreshed first, because the session behind it may well live.
 *
 * A 401 can arrive whatever `/me` said at startup: during the flip, UDE may enforce while
 * questions does not yet (SB-25). So the route to the sign-in page answers to the 401 itself
 * (`session.ts`), never to the startup answer.
 *
 * `/me` also names the company the session works in and the companies the person may work in
 * (SB-318). A switch loads the page again (SB-321, `company.ts`).
 */
import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {Navigate, useLocation} from 'react-router-dom';

import {pathAfterSwitch, requestCompanySwitch} from './company';
import {
    noteTokenLifetime, onSignInRequired, refreshSession, signInPath, SIGN_IN_PATH, SignInReason, watchActivity,
} from './session';

export interface AuthUser {
    id: string;
    email: string | null;
    name: string | null;
    /** The PLATFORM role: `admin` is the SuccessBuilder team (SB-24). Never a company role. */
    role: string;
}

/** A company the person may work in, with their role there (null: the platform role, no member). */
export interface AuthCompany {
    id: string;
    name: string | null;
    role: 'admin' | 'member' | null;
}

interface Me {
    auth_enabled: boolean;
    login_available: boolean;
    providers: string[];
    /** A first sign-in nobody pre-approved makes a company of its own (SB-323). */
    signup_open?: boolean;
    authenticated: boolean;
    user: AuthUser | null;
    company?: AuthCompany | null;
    companies?: AuthCompany[];
    expires_in: number | null;
}

export interface AuthState {
    status: 'loading' | 'ready';
    authEnabled: boolean;
    loginAvailable: boolean;
    providers: string[];
    /** Anyone may sign in, and a first sign-in makes a company named after the person (SB-323). */
    signupOpen: boolean;
    user: AuthUser | null;
    /** The company the session works in, as the token says (what UDE decides on). */
    company: AuthCompany | null;
    /** Every company the person may work in: their memberships, or all of them for the platform role. */
    companies: AuthCompany[];
    /** A request found no session, whatever `/me` said at startup; `signInReason` says why. */
    signInRequired: boolean;
    signInReason?: SignInReason;
    signOut: () => Promise<void>;
    /** Work in another company. Loads the page again; resolves only when the switch was refused. */
    switchCompany: (companyId: string) => Promise<'refused' | 'failed'>;
    /** Ask `/me` again, after a change to the company's name or the person's place in it. */
    reloadMe: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
    status: 'loading',
    authEnabled: false,
    loginAvailable: false,
    providers: [],
    signupOpen: false,
    user: null,
    company: null,
    companies: [],
    signInRequired: false,
    signOut: async () => {},
    switchCompany: async () => 'failed',
    reloadMe: async () => {},
});

export function useAuth(): AuthState {
    return useContext(AuthContext);
}

/**
 * Whether the person holds the platform role, the SuccessBuilder team's (SB-24, SB-322): the corpus
 * tooling, Adressesøk's limits and analysis, the admin pages. True while sign-in is off, as before.
 * The API's 403 is the guard; this only keeps the controls off a customer's screen.
 */
export function usePlatformRole(): boolean {
    const {authEnabled, user} = useContext(AuthContext);
    return !authEnabled || user?.role === 'admin';
}

/** The app renders nothing until this answers, so it may not wait on a questions that hangs. */
const ME_TIMEOUT_MS = 5000;

async function readMe(): Promise<Me | null> {
    try {
        const response = await fetch('/api/auth/me', {
            credentials: 'same-origin',
            headers: {Accept: 'application/json'},
            signal: AbortSignal.timeout(ME_TIMEOUT_MS),
        });
        return response.ok ? await response.json() : null;
    } catch {
        return null;  // unreachable or too slow: render the app; a backend that enforces answers 401
    }
}

export function AuthProvider({children}: {children: ReactNode}) {
    const [me, setMe] = useState<Me | null>(null);
    const [required, setRequired] = useState<{reason?: SignInReason} | null>(null);
    const [status, setStatus] = useState<'loading' | 'ready'>('loading');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            let answer = await readMe();
            if (answer?.auth_enabled && !answer.authenticated && answer.login_available) {
                if ((await refreshSession()) === 'ok') answer = await readMe();
            }
            if (cancelled) return;
            noteTokenLifetime(answer?.expires_in);
            setMe(answer);
            setStatus('ready');
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // No navigate() here: react-router runs a navigation as a transition, so the urgent update
    // that clears the user would render first, and the gate's own redirect would win without the
    // reason. Both go in one urgent update, and RequireSignIn makes the one redirect.
    useEffect(() => onSignInRequired(reason => {
        setRequired({reason});
        setMe(previous => (previous ? {...previous, authenticated: false, user: null} : previous));
    }), []);

    const enforced = Boolean(me?.auth_enabled && me.user);
    useEffect(() => {
        watchActivity(enforced);
        return () => watchActivity(false);
    }, [enforced]);

    const signOut = useCallback(async () => {
        await fetch('/api/auth/logout', {method: 'POST', credentials: 'same-origin'}).catch(() => undefined);
        noteTokenLifetime(null);
        // A full page load, not a route change: nothing the last person loaded stays in memory,
        // which matters on a shared PC. A route change would also race the page being left,
        // which renders signed out first and sends the sign-in page a way back to itself.
        window.location.replace(SIGN_IN_PATH);
    }, []);

    const switchCompany = useCallback(async (companyId: string): Promise<'refused' | 'failed'> => {
        const outcome = await requestCompanySwitch(companyId);
        if (outcome === 'ok') {
            // A page load, as at sign-out: nothing the last company loaded stays on screen.
            window.location.assign(pathAfterSwitch(window.location.pathname, window.location.search));
            return new Promise<never>(() => undefined);
        }
        if (outcome === 'ended') window.location.replace(signInPath(window.location.pathname, 'session_expired'));
        return outcome === 'refused' ? 'refused' : 'failed';
    }, []);

    const reloadMe = useCallback(async () => {
        const answer = await readMe();
        if (answer) setMe(answer);
    }, []);

    const value = useMemo<AuthState>(() => ({
        status,
        authEnabled: Boolean(me?.auth_enabled),
        loginAvailable: Boolean(me?.login_available),
        providers: me?.providers ?? [],
        signupOpen: Boolean(me?.signup_open),
        user: me?.user ?? null,
        company: me?.company ?? null,
        companies: me?.companies ?? [],
        signInRequired: required !== null,
        signInReason: required?.reason,
        signOut,
        switchCompany,
        reloadMe,
    }), [status, me, required, signOut, switchCompany, reloadMe]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Renders the app for a signed-in person, or for everyone while sign-in is not enforced. */
export function RequireSignIn({children}: {children: ReactNode}) {
    const auth = useAuth();
    const location = useLocation();
    if (auth.status === 'loading') return null;
    if (auth.signInRequired || (auth.authEnabled && !auth.user)) {
        const here = location.pathname + location.search + location.hash;
        return <Navigate to={signInPath(here, auth.signInReason)} replace/>;
    }
    return <>{children}</>;
}
